import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Copy, MessageCircle, Plus, ShoppingCart, Trash2, TriangleAlert } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;
const BLANK = { name: "", price: "" };

/**
 * Turn an agreed prescription into a real order: pick catalogue medicines, add any
 * hand-prepared line, set the course instructions per line, type the delivery
 * address, then hand the patient a private pay link. Every line stays editable
 * until the order is created, and a prescription can only be converted once.
 */
export const RxConvert = ({ upload, onConverted }) => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState([]);
  const [custom, setCustom] = useState(BLANK);
  const [address, setAddress] = useState("");
  const [coupon, setCoupon] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}); }, []);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 6);
  }, [search, products]);

  const addProduct = (p) => {
    if (lines.some((l) => l.product_id === p.id)) return toast.info(`${p.name} is already on the order`);
    setLines((l) => [...l, {
      product_id: p.id, name: p.name, price: p.price, quantity: 1,
      instructions: "", stock: p.stock,
    }]);
    setSearch("");
  };

  const addCustom = () => {
    const price = parseFloat(custom.price);
    if (!custom.name.trim() || !(price > 0)) return toast.error("A hand-prepared line needs a name and a price");
    setLines((l) => [...l, { product_id: null, name: custom.name.trim(), price, quantity: 1, instructions: "" }]);
    setCustom(BLANK);
  };

  const edit = (i, patch) => setLines((l) => l.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const drop = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const subtotal = lines.reduce((sum, l) => sum + Number(l.price) * Number(l.quantity || 0), 0);
  const shortLines = lines.filter((l) => l.product_id && Number(l.quantity) > Number(l.stock ?? 0));

  const create = async () => {
    if (!lines.length) return toast.error("Add at least one medicine");
    if (!address.trim()) return toast.error("Enter the delivery address for the shipping label");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/prescription-uploads/${upload.id}/order`, {
        items: lines.map((l) => ({
          product_id: l.product_id, name: l.product_id ? null : l.name,
          price: l.product_id ? null : l.price, quantity: Number(l.quantity),
          instructions: l.instructions,
        })),
        address: address.trim(),
        coupon_code: coupon.trim(),
      });
      setResult(data);
      toast.success(`Order created · ${money(data.total)}`);
      onConverted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const existing = result || (upload.order ? { ...upload.order, order_id: upload.order.id } : null);

  if (existing) {
    const link = existing.pay_url || "";
    const wa = `https://wa.me/${(upload.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(
      `Namaste ${upload.name.split(" ")[0]}, your Immortal Vedas order is ready. Amount ${money(existing.total)}. Pay securely here: ${link}`
    )}`;
    return (
      <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="rx-convert-done">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10">
            Order #{String(existing.order_id).slice(0, 8)}
          </Badge>
          <span className="text-sm">{money(existing.total)}</span>
          <Badge variant="outline" className="text-[10px] capitalize">{existing.payment_status || "pending"}</Badge>
          {existing.has_backorder && (
            <Badge variant="outline" className="border-secondary/50 text-secondary bg-secondary/10 text-[10px]">
              <TriangleAlert size={10} className="mr-1" /> backorder
            </Badge>
          )}
        </div>
        {(existing.backorder_items || []).length > 0 && (
          <p className="text-xs text-secondary" data-testid="rx-convert-backorder">
            Being restocked: {existing.backorder_items.join(", ")}
          </p>
        )}
        {link ? (
          <>
            <p className="text-xs text-muted-foreground break-all" data-testid="rx-convert-pay-url">{link}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm" variant="outline" className="btn-pill h-9"
                onClick={() => { navigator.clipboard.writeText(link); toast.success("Pay link copied"); }}
                data-testid="rx-convert-copy-link"
              >
                <Copy size={13} className="mr-1.5" /> Copy pay link
              </Button>
              <a href={wa} target="_blank" rel="noreferrer" data-testid="rx-convert-whatsapp">
                <Button size="sm" className="btn-action h-9">
                  <MessageCircle size={13} className="mr-1.5" /> Send on WhatsApp
                </Button>
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground">
              The link works for {existing.pay_link_hours || 72} hours and needs no login or OTP.
            </p>
          </>
        ) : (
          <p className="text-xs text-primary">This order is paid — the invoice and WhatsApp confirmation have gone out.</p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border p-4 space-y-4" data-testid="rx-convert">
      <div className="flex items-center gap-2">
        <ShoppingCart size={15} className="text-primary" />
        <span className="overline">Create the order</span>
      </div>

      <div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search your products…"
          className="bg-transparent"
          data-testid="rx-convert-search"
        />
        {matches.length > 0 && (
          <div className="mt-2 rounded-lg border border-border divide-y divide-border">
            {matches.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => addProduct(p)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent/50 flex items-center justify-between gap-3"
                data-testid={`rx-convert-add-${p.id}`}
              >
                <span className="truncate">{p.name}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {money(p.price)} · {p.stock} in stock
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-end">
        <Input value={custom.name} onChange={(e) => setCustom((c) => ({ ...c, name: e.target.value }))}
               placeholder="Hand-prepared medicine" className="bg-transparent" data-testid="rx-convert-custom-name" />
        <Input value={custom.price} onChange={(e) => setCustom((c) => ({ ...c, price: e.target.value }))}
               placeholder="₹" inputMode="decimal" className="bg-transparent w-24" data-testid="rx-convert-custom-price" />
        <Button variant="outline" className="btn-pill h-10" onClick={addCustom} data-testid="rx-convert-custom-add">
          <Plus size={14} />
        </Button>
      </div>

      {lines.length > 0 && (
        <div className="space-y-2">
          {lines.map((line, i) => (
            <div key={`${line.name}-${i}`} className="rounded-lg border border-border p-3" data-testid={`rx-line-${i}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate flex-1">{line.name}</span>
                <span className="text-xs text-muted-foreground">{money(line.price)}</span>
                <Input
                  value={line.quantity}
                  onChange={(e) => edit(i, { quantity: e.target.value.replace(/\D/g, "") || 1 })}
                  inputMode="numeric"
                  className="w-16 h-9 bg-transparent text-center"
                  data-testid={`rx-line-qty-${i}`}
                />
                <Button size="sm" variant="ghost" className="h-9 px-2 text-destructive"
                        onClick={() => drop(i)} data-testid={`rx-line-remove-${i}`}>
                  <Trash2 size={13} />
                </Button>
              </div>
              <Input
                value={line.instructions}
                onChange={(e) => edit(i, { instructions: e.target.value })}
                placeholder="Course & dosage, e.g. 15-day course, 1-0-1 after meals"
                className="mt-2 h-9 bg-transparent text-xs"
                data-testid={`rx-line-instructions-${i}`}
              />
              {line.product_id && Number(line.quantity) > Number(line.stock ?? 0) && (
                <p className="text-[11px] text-secondary mt-1.5" data-testid={`rx-line-short-${i}`}>
                  Only {line.stock} in stock — this line will be marked backorder, not dropped.
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <div>
        <label className="overline">Delivery address</label>
        <Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2}
                  placeholder="House, street, city, PIN" className="mt-2 bg-transparent"
                  data-testid="rx-convert-address" />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="overline">Promo code (optional)</label>
          <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                 className="mt-2 bg-transparent w-40" data-testid="rx-convert-coupon" />
        </div>
        <div className="ml-auto text-right">
          <div className="overline">Items total</div>
          <div className="font-display text-2xl" data-testid="rx-convert-subtotal">{money(subtotal)}</div>
        </div>
      </div>

      {shortLines.length > 0 && (
        <p className="text-xs text-secondary" data-testid="rx-convert-short-note">
          {shortLines.length} line(s) will go out as backorder — the patient can still pay.
        </p>
      )}

      <Button onClick={create} disabled={busy} className="btn-action h-11 w-full" data-testid="rx-convert-create">
        {busy ? "Creating…" : "Create order & get pay link"}
      </Button>
      <p className="text-[11px] text-muted-foreground">
        GST, invoice, shipping label and the customer's WhatsApp updates all behave exactly as a website order.
      </p>
    </div>
  );
};

export default RxConvert;
